import { DbEvent } from '../types';

export const FLAT_RATE_BASE = 1500;
export const REVENUE_SHARE_BASE = 1000;
export const REVENUE_SHARE_PCT = 0.5;
export const RESTROOM_FEE = 300;
export const INSURANCE_WAIVER_FEE = 164;
export const CLEANING_FEE_AMOUNT = 250;
export const SECURITY_FEE_AMOUNT = 200;
export const OVERAGE_RATE = 100;
export const LATE_INTEREST_RATE = 0.10;

export function calcBase(event: Pick<DbEvent, 'pricing_model' | 'actual_revenue'>): number {
  if (event.pricing_model === 'flat_rate') return FLAT_RATE_BASE;
  const rev = event.actual_revenue ?? 0;
  if (rev <= REVENUE_SHARE_BASE) return REVENUE_SHARE_BASE;
  return REVENUE_SHARE_BASE + (rev - REVENUE_SHARE_BASE) * REVENUE_SHARE_PCT;
}

export function calcTotal(event: Partial<DbEvent>): number {
  const base = calcBase({
    pricing_model: event.pricing_model ?? 'flat_rate',
    actual_revenue: event.actual_revenue ?? null,
  });
  return (
    base +
    (event.restroom_fee ?? 0) +
    (event.insurance_fee ?? 0) +
    (event.cleaning_fee ?? 0) +
    (event.security_fee ?? 0) +
    (event.overage_fee ?? 0) +
    (event.damage_fee ?? 0)
  );
}

export function calcBalance(event: Partial<DbEvent>): number {
  return calcTotal(event) - (event.amount_paid ?? 0);
}

export function isOverdue(event: Partial<DbEvent>): boolean {
  if (!event.payment_due_date) return false;
  const balance = calcBalance(event);
  return balance > 0 && new Date() > new Date(event.payment_due_date + 'T00:00:00');
}

export function calcLateInterest(event: Partial<DbEvent>): number {
  if (!isOverdue(event)) return 0;
  const balance = calcBalance(event);
  if (balance <= 0) return 0;
  const dueDate = new Date(event.payment_due_date! + 'T00:00:00');
  const daysOverdue = Math.max(0, Math.floor((Date.now() - dueDate.getTime()) / 86400000));
  return balance * LATE_INTEREST_RATE * (daysOverdue / 365);
}

export function fmtMoney(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function fmtDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[m-1]} ${d}, ${y}`;
}

export function fmtTime(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2,'0')} ${ampm}`;
}
